import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  CircularProgress
} from "@mui/material";
import { getPipelineCandidatesService } from "../redux/service/jobService";
import { CheckCircle, Done, UploadFile } from "@mui/icons-material";
import constants from '../helpers/constants.json';
import { updateCandidateJoiningDetails } from "../redux/actions/jobActions";
import { useDispatch } from "react-redux";
import { uploadFile } from "../redux/actions/userActions";
import { uploadSingleFileService } from "../redux/service/userService";
import showToast from "../utils/toast";

export default function JoiningDetailsModal({ open, onClose, jobPostId, jobTitle, candidate, closeTimelineModalCallback }) {
  const dispatch = useDispatch();
  const [monthlyCTC, setMonthlyCTC] = useState(candidate.monthlyCTC);
  const [requiresReplacement, setRequiresReplacement] = useState(false);
  const [candidateOptions, setCandidateOptions] = useState([]);
  const [replacementCandidateId, setReplacementCandidateId] = useState(candidate.replacedCandidateId);
  const [paymentReceived, setPaymentReceived] = useState(candidate.paymentAmount ? "yes" : "no");
  const [amount, setAmount] = useState(candidate.paymentAmount);
  const [paidVia, setPaidVia] = useState(candidate.paymentMadeVia);
  const [offerLetterUploading, setOfferLetterUploading] = useState(false);
  const [offerLetterDocId, setOfferLetterDocId] = useState(candidate.offerLetterDocId);
  const [aadharCardDocId, setAadharCardDocId] = useState(candidate.aadharCardDocId);

  useEffect(() => {
    async function fetchCandidateLeftList() {

      const response = await getPipelineCandidatesService(jobPostId, {
        "status": "CANDIDATE_LEFT"
      });
      const list = response.data || [];

      setCandidateOptions(list);
      setRequiresReplacement(list.length > 0);  // auto-set boolean
    }

    fetchCandidateLeftList();
  }, []);


  const handleSaveJoiningDetails = async () => {
    if (offerLetterUploading) return showToast("Please wait, offer letter is being uploaded.", 'warning');

    if (paymentReceived == "yes" && !amount && !paidVia) return showToast("Please fill Amount and Paid via if Payment is received.", 'error');

    const payload = {
      'monthlyCTC': monthlyCTC,
      'replacedCandidateId': requiresReplacement ? replacementCandidateId : null,
      'paymentAmount': paymentReceived == "yes" ? amount : null,
      'paymentMadeVia': paymentReceived == "yes" ? paidVia : null,
      'offerLetterDocId': offerLetterDocId ? offerLetterDocId : null
    }

    dispatch(updateCandidateJoiningDetails(candidate.jobPostCandidateId, payload, jobPostId));
    onClose();
    if (closeTimelineModalCallback) {
      closeTimelineModalCallback();
    }
  };

  const handleAadharCardUpload = async (event) => {
    const selectedFiles = event.target.files;

    await dispatch(uploadFile(selectedFiles, candidate.candidateId, constants.docType.aadharCard));
    // just to show that aadhar card is uploaded
    setAadharCardDocId("Uploaded");
  }

  const handleOfferLetterUpload = async (event) => {
    const selectedFile = event.target.files[0];
    setOfferLetterUploading(true);
    let docRes = await uploadSingleFileService(selectedFile, candidate.candidateId, constants.docType.offerLetter);
    setOfferLetterUploading(false);
    if (docRes.success) {
      showToast("Offer Letter uploaded successfully", 'success');
      setOfferLetterDocId(docRes?.data?.id);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Update Joining Details - {candidate.candidateName}</DialogTitle>

      <DialogContent dividers>

        {/* Row: Monthly CTC + Select Replacement For */}
        <Box display="flex" gap={2} mb={2}>
          <TextField
            label="Monthly CTC"
            fullWidth
            value={monthlyCTC}
            type="number"
            onChange={(e) => setMonthlyCTC(e.target.value)}
          />

          {requiresReplacement &&
            <FormControl fullWidth>
              <InputLabel>Select Replacement For</InputLabel>
              <Select
                label="Select Replacement Candidate"
                value={replacementCandidateId}
                onChange={(e) => setReplacementCandidateId(e.target.value)}
              >
                {candidateOptions.map((c) => (
                  <MenuItem key={c.candidateId} value={c.candidateId}>
                    {c.candidateName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          }
        </Box>

        {/* Payment Received */}
        <Typography sx={{ mb: 1 }}>Payment Received?</Typography>
        <RadioGroup
          row
          value={paymentReceived}
          onChange={(e) => setPaymentReceived(e.target.value)}
          sx={{ mb: 2 }}
        >
          <FormControlLabel value="yes" control={<Radio />} label="Yes" />
          <FormControlLabel value="no" control={<Radio />} label="No" />
        </RadioGroup>

        {/* If payment received = yes → Amount + Paid Via */}
        {paymentReceived === "yes" && (
          <Box display="flex" gap={2} mb={2}>
            <TextField
              label="Amount"
              type="number"
              fullWidth
              required
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />

            <FormControl fullWidth required>
              <InputLabel>Paid Via</InputLabel>
              <Select
                value={paidVia}
                label="Paid Via"
                onChange={(e) => setPaidVia(e.target.value)}
              >
                {constants.paymentMethods.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}

        {/* Upload Documents → Aadhaar + Offer Letter */}
        <Typography mb={1}>Upload Documents:</Typography>

        <Box display="flex" gap={2}>
          <Button variant="outlined" component="label" startIcon={<UploadFile />} fullWidth>
            Aadhar Card
            <input
              accept="image/*, application/pdf, .doc, .docx"
              hidden
              type="file"
              multiple
              onChange={(event) => handleAadharCardUpload(event)}
            />
            {aadharCardDocId &&
              <div style={{ position: 'absolute', right: '-10px', top: '-10px', backgroundColor: 'lightgrey', display: 'flex', flexDirection: 'row', alignItems: 'center', padding: '2px', borderRadius: '4px' }}>
                <span style={{ fontSize: '12px', textTransform: 'capitalize', color: 'black' }}>Uploaded</span>
                <CheckCircle style={{ color: 'green', fontSize: '18px' }} />
              </div>
            }
          </Button>

          <Button disabled={offerLetterUploading} variant="outlined" component="label" startIcon={offerLetterUploading ? <CircularProgress size="16px" /> : <UploadFile />} fullWidth>
            {offerLetterUploading ? "Uploading" : "Offer Letter"}
            <input
              accept="image/*, application/pdf, .doc, .docx"
              hidden
              type="file"
              onChange={(event) => handleOfferLetterUpload(event)}
            />
            {offerLetterDocId &&
              <div style={{ position: 'absolute', right: '-10px', top: '-10px', backgroundColor: 'lightgrey', display: 'flex', flexDirection: 'row', alignItems: 'center', padding: '2px', borderRadius: '4px' }}>
                <span style={{ fontSize: '12px', textTransform: 'capitalize', color: 'black' }}>Uploaded</span>
                <CheckCircle style={{ color: 'green', fontSize: '18px' }} />
              </div>
            }
          </Button>
        </Box>

      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button onClick={handleSaveJoiningDetails} variant="contained">Save</Button>
      </DialogActions>
    </Dialog>
  );
}
